import type { BinnedColumn, ColumnBox } from '../geometry/binnedColumns';
import { aggregate } from '../data/collapseCoincident';
import type { Rgb } from './colorScale';
import { parseColor } from './panelShading';
import { toGradientLookup, type SurfaceColoring } from './surfacePaint';

/** One band of a column: a color and the share of the column's height it takes, bottom first. */
export type ColumnSegment = { color: Rgb; share: number };

/** A column's colors, keyed by its lattice index. A single segment paints it solid. */
export type ColumnPaints = ReadonlyMap<number, ColumnSegment[]>;

type ColumnPaintArgs = {
  coloring: SurfaceColoring;
  columns: BinnedColumn[];
  unknownColor: Rgb;
};

const toGradientSegments = (
  {
    valuesById,
    domain,
    colors,
  }: Extract<SurfaceColoring, { kind: 'gradient' }>,
  { columns, unknownColor }: ColumnPaintArgs
): ColumnPaints => {
  const colorOf = toGradientLookup(domain, colors);
  return new Map(
    columns.map(column => {
      const values = column.points.flatMap(({ componentId }) => {
        const value = valuesById.get(componentId);
        return value === undefined ? [] : [value];
      });
      const color =
        values.length > 0 ? colorOf(aggregate(values, 'mean')) : null;
      return [column.index, [{ color: color ?? unknownColor, share: 1 }]];
    })
  );
};

const toStackedSegments = (
  colors: Map<string, Rgb>,
  column: BinnedColumn,
  unknownColor: Rgb
): ColumnSegment[] => {
  const counts = column.points.reduce((tally, { componentId }) => {
    const color = colors.get(componentId) ?? unknownColor;
    const key = color.join(',');
    const entry = tally.get(key) ?? { color, count: 0 };
    return tally.set(key, { color, count: entry.count + 1 });
  }, new Map<string, { color: Rgb; count: number }>());
  const total = column.points.length || 1;

  return [...counts.values()]
    .sort((a, b) => b.count - a.count)
    .map(({ color, count }) => ({ color, share: count / total }));
};

const toRegionSegments = (
  { colorsById }: Extract<SurfaceColoring, { kind: 'regions' }>,
  { columns, unknownColor }: ColumnPaintArgs
): ColumnPaints => {
  const colors = new Map(
    [...colorsById].flatMap(([componentId, css]) => {
      const rgb = parseColor(css);
      return rgb ? [[componentId, rgb] as const] : [];
    })
  );
  return new Map(
    columns.map(column => [
      column.index,
      toStackedSegments(colors, column, unknownColor),
    ])
  );
};

/**
 * Each column's colors, or null when the columns are colored by height.
 *
 * A gradient colors a column by the average of the rule's field over its components, whatever the
 * height reduces them by, since the rule's scale is defined for one component's value. Category colors cannot be reduced, so a column holding several stacks them: one band per
 * color, sized by its share of the column's components, the most common at the bottom.
 */
export const toColumnPaints = (args: ColumnPaintArgs): ColumnPaints | null => {
  const { coloring } = args;
  if (coloring.kind === 'gradient') return toGradientSegments(coloring, args);
  if (coloring.kind === 'regions') return toRegionSegments(coloring, args);
  return null;
};

/** A column's box cut into one box per segment, stacked upward from its bottom. */
export const toSegmentBoxes = (
  box: ColumnBox,
  segments: ColumnSegment[]
): { box: ColumnBox; color: Rgb }[] => {
  const [bottom, top] = box.z;
  const height = top - bottom;
  return segments.reduce<{
    at: number;
    boxes: { box: ColumnBox; color: Rgb }[];
  }>(
    ({ at, boxes }, { color, share }) => {
      const next = at + height * share;
      return {
        at: next,
        boxes: [...boxes, { box: { ...box, z: [at, next] }, color }],
      };
    },
    { at: bottom, boxes: [] }
  ).boxes;
};
